import { log } from './production-logger';

/**
 * Feature Flags Service
 * Provides safe, gradual rollout capabilities for major refactoring changes
 * Supports A/B testing, user-based flags, and percentage rollouts
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

// Storage key for local feature flags cache
const FEATURE_FLAGS_CACHE_KEY = '@feature_flags:cache';
const FEATURE_FLAGS_CONFIG_KEY = '@feature_flags:config';

export enum FeatureFlag {
  // Service Refactoring Flags
  USE_NEW_EXTRACTION_SERVICE = 'use_new_extraction_service',
  USE_NEW_SUPPLIER_SERVICE = 'use_new_supplier_service',
  USE_NEW_INVOICE_SERVICE = 'use_new_invoice_service',
  
  // Terminology Migration Flags
  USE_PURCHASE_TERMINOLOGY = 'use_purchase_terminology',
  SHOW_LEGACY_INVOICE_TERMS = 'show_legacy_invoice_terms',
  
  // UI/UX Improvements
  USE_ENHANCED_DASHBOARD = 'use_enhanced_dashboard',
  ENABLE_BULK_OPERATIONS = 'enable_bulk_operations',
  USE_OPTIMIZED_LISTS = 'use_optimized_lists',
  
  // Performance Features
  ENABLE_LIST_VIRTUALIZATION = 'enable_list_virtualization',
  USE_AGGRESSIVE_CACHING = 'use_aggressive_caching',
  ENABLE_IMAGE_COMPRESSION = 'enable_image_compression',
  
  // AI/Extraction Features - Removed OCR prompts (now default)
  
  // Beta Features
  ENABLE_ADVANCED_ANALYTICS = 'enable_advanced_analytics',
  ENABLE_AI_SUGGESTIONS = 'enable_ai_suggestions',
  ENABLE_VOICE_COMMANDS = 'enable_voice_commands'
}

export enum RolloutStrategy {
  DISABLED = 'disabled',           // Feature is off for everyone
  ENABLED = 'enabled',             // Feature is on for everyone
  PERCENTAGE = 'percentage',       // Feature is on for X% of users
  USER_LIST = 'user_list',        // Feature is on for specific users
  ORGANIZATION = 'organization',   // Feature is on for specific organizations
  PLATFORM = 'platform',         // Feature is on for specific platforms
  GRADUAL = 'gradual'             // Gradually increasing percentage over time
}

export interface FeatureFlagConfig {
  flag: FeatureFlag;
  strategy: RolloutStrategy;
  value: boolean | number | string[];
  description: string;
  createdAt: number;
  updatedAt: number;
  // Rollback configuration
  rollbackEnabled?: boolean;
  rollbackThreshold?: number; // Error rate threshold for automatic rollback
  monitoringEnabled?: boolean;
}

export interface FeatureFlagContext {
  userId?: string;
  organizationId?: string;
  platform: string;
  version: string;
  userRole?: string;
  beta?: boolean;
}

class FeatureFlagsService {
  private cache: Map<FeatureFlag, boolean> = new Map();
  private config: Map<FeatureFlag, FeatureFlagConfig> = new Map();
  private initialized = false;

  /**
   * Initialize the feature flags service
   */
  async initialize(): Promise<void> {
    try {
      await this.loadFromCache();
      await this.loadDefaultConfig();
      this.initialized = true;
      log.debug('[FeatureFlags] Service initialized successfully');
    } catch (error) {
      log.error('[FeatureFlags] Initialization error:', error);
      // Continue with default configuration
      await this.loadDefaultConfig();
      this.initialized = true;
    }
  }

  /**
   * Check if a feature flag is enabled for the current context
   */
  async isEnabled(flag: FeatureFlag, context?: FeatureFlagContext): Promise<boolean> {
    if (!this.initialized) {
      await this.initialize();
    }

    try {
      // Check cache first
      if (this.cache.has(flag)) {
        return this.cache.get(flag)!;
      }

      // Get configuration
      const config = this.config.get(flag);
      if (!config) {
        log.warn(`[FeatureFlags] No configuration found for flag: ${flag}`);
        return false;
      }

      // Evaluate based on rollout strategy
      const enabled = await this.evaluateFlag(config, context);
      
      // Cache the result
      this.cache.set(flag, enabled);
      
      return enabled;
    } catch (error) {
      log.error(`[FeatureFlags] Error evaluating flag ${flag}:`, error);
      return false; // Fail safe - return false if evaluation fails
    }
  }

  /**
   * Enable a feature flag
   */
  async enableFlag(flag: FeatureFlag, strategy: RolloutStrategy = RolloutStrategy.ENABLED, value?: any): Promise<void> {
    const config: FeatureFlagConfig = {
      flag,
      strategy,
      value: value ?? true,
      description: `Enabled ${flag}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      rollbackEnabled: true,
      monitoringEnabled: true
    };

    this.config.set(flag, config);
    this.cache.delete(flag); // Clear cache to force re-evaluation
    await this.saveConfig();
    
    log.debug(`[FeatureFlags] Enabled flag: ${flag} with strategy: ${strategy}`);
  }

  /**
   * Disable a feature flag
   */
  async disableFlag(flag: FeatureFlag): Promise<void> {
    const config = this.config.get(flag);
    if (config) {
      config.strategy = RolloutStrategy.DISABLED;
      config.value = false;
      config.updatedAt = Date.now();
      
      this.config.set(flag, config);
      this.cache.delete(flag);
      await this.saveConfig();
    }
    
    log.debug(`[FeatureFlags] Disabled flag: ${flag}`);
  }

  /**
   * Set percentage rollout for a feature flag
   */
  async setPercentageRollout(flag: FeatureFlag, percentage: number): Promise<void> {
    if (percentage < 0 || percentage > 100) {
      throw new Error('Percentage must be between 0 and 100');
    }

    await this.enableFlag(flag, RolloutStrategy.PERCENTAGE, percentage);
    log.debug(`[FeatureFlags] Set ${flag} to ${percentage}% rollout`);
  }

  /**
   * Get all feature flag statuses
   */
  async getAllFlags(context?: FeatureFlagContext): Promise<Record<string, boolean>> {
    const results: Record<string, boolean> = {};
    
    for (const flag of Object.values(FeatureFlag)) {
      results[flag] = await this.isEnabled(flag, context);
    }
    
    return results;
  }

  /**
   * Clear all cached flags (force re-evaluation)
   */
  clearCache(): void {
    this.cache.clear();
    log.debug('[FeatureFlags] Cache cleared');
  }

  // Private methods

  private async evaluateFlag(config: FeatureFlagConfig, context?: FeatureFlagContext): Promise<boolean> {
    const effectiveContext = {
      platform: Platform.OS,
      version: '1.0.0', // TODO: Get from app config
      ...context
    };

    switch (config.strategy) {
      case RolloutStrategy.DISABLED:
        return false;

      case RolloutStrategy.ENABLED:
        return true;

      case RolloutStrategy.PERCENTAGE:
        return this.evaluatePercentage(config.value as number, effectiveContext);

      case RolloutStrategy.USER_LIST:
        return this.evaluateUserList(config.value as string[], effectiveContext);

      case RolloutStrategy.ORGANIZATION:
        return this.evaluateOrganization(config.value as string[], effectiveContext);

      case RolloutStrategy.PLATFORM:
        return this.evaluatePlatform(config.value as string[], effectiveContext);

      case RolloutStrategy.GRADUAL:
        return this.evaluateGradual(config, effectiveContext);

      default:
        log.warn(`[FeatureFlags] Unknown strategy: ${config.strategy}`);
        return false;
    }
  }

  private evaluatePercentage(percentage: number, context: FeatureFlagContext): boolean {
    // Use user ID for consistent percentage calculation
    const userId = context.userId || 'anonymous';
    const hash = this.hashString(userId);
    return (hash % 100) < percentage;
  }

  private evaluateUserList(userIds: string[], context: FeatureFlagContext): boolean {
    return context.userId ? userIds.includes(context.userId) : false;
  }

  private evaluateOrganization(orgIds: string[], context: FeatureFlagContext): boolean {
    return context.organizationId ? orgIds.includes(context.organizationId) : false;
  }

  private evaluatePlatform(platforms: string[], context: FeatureFlagContext): boolean {
    return platforms.includes(context.platform);
  }

  private evaluateGradual(config: FeatureFlagConfig, context: FeatureFlagContext): boolean {
    // Simple gradual rollout: increase 10% per week
    const daysSinceCreated = (Date.now() - config.createdAt) / (1000 * 60 * 60 * 24);
    const weeksSinceCreated = Math.floor(daysSinceCreated / 7);
    const currentPercentage = Math.min(weeksSinceCreated * 10, 100);
    
    return this.evaluatePercentage(currentPercentage, context);
  }

  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  private async loadFromCache(): Promise<void> {
    try {
      const cached = await AsyncStorage.getItem(FEATURE_FLAGS_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        this.cache = new Map(Object.entries(data) as Array<[FeatureFlag, boolean]>);
      }

      const configData = await AsyncStorage.getItem(FEATURE_FLAGS_CONFIG_KEY);
      if (configData) {
        const configs = JSON.parse(configData);
        this.config = new Map(
          Object.entries(configs).map(([key, value]) => [key as FeatureFlag, value as FeatureFlagConfig])
        );
      }
    } catch (error) {
      log.warn('[FeatureFlags] Failed to load from cache:', error);
    }
  }

  private async saveConfig(): Promise<void> {
    try {
      const configObj = Object.fromEntries(this.config.entries());
      await AsyncStorage.setItem(FEATURE_FLAGS_CONFIG_KEY, JSON.stringify(configObj));
    } catch (error) {
      log.error('[FeatureFlags] Failed to save config:', error);
    }
  }

  private async loadDefaultConfig(): Promise<void> {
    // Default configuration for safe rollout
    const defaultConfigs: FeatureFlagConfig[] = [
      // Service refactoring flags - start disabled for safety
      {
        flag: FeatureFlag.USE_NEW_EXTRACTION_SERVICE,
        strategy: RolloutStrategy.DISABLED,
        value: false,
        description: 'Use new refactored extraction service',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        rollbackEnabled: true,
        monitoringEnabled: true
      },
      {
        flag: FeatureFlag.USE_NEW_SUPPLIER_SERVICE,
        strategy: RolloutStrategy.DISABLED,
        value: false,
        description: 'Use new refactored supplier service',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        rollbackEnabled: true,
        monitoringEnabled: true
      },
      {
        flag: FeatureFlag.USE_NEW_INVOICE_SERVICE,
        strategy: RolloutStrategy.DISABLED,
        value: false,
        description: 'Use new refactored invoice service',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        rollbackEnabled: true,
        monitoringEnabled: true
      },
      // Terminology migration - enabled by default as it's safe
      {
        flag: FeatureFlag.USE_PURCHASE_TERMINOLOGY,
        strategy: RolloutStrategy.ENABLED,
        value: true,
        description: 'Use purchase terminology instead of invoice',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        rollbackEnabled: true,
        monitoringEnabled: false
      },
      // OCR prompts are now default - removed A/B testing
      // Performance improvements - start with low percentage
      {
        flag: FeatureFlag.USE_OPTIMIZED_LISTS,
        strategy: RolloutStrategy.PERCENTAGE,
        value: 25,
        description: 'Use optimized list components',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        rollbackEnabled: true,
        monitoringEnabled: true
      }
    ];

    for (const config of defaultConfigs) {
      if (!this.config.has(config.flag)) {
        this.config.set(config.flag, config);
      }
    }
  }
}

// Export singleton instance
export const featureFlagsService = new FeatureFlagsService();
export default featureFlagsService;